import { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import GeoStatusAlert from 'components/GeoStatusAlert';
import { DateTime } from 'luxon';
import {
  BumpResourceSheetDto,
  JobPhaseBumpApprovalStatus,
} from '@vatos-pas/common';

import { Show } from 'components/Show';
import { convertTo12HourFormat } from 'modules/master-sheet-v2/utils/convertTo12HourFormat';
import {
  CAN_APPROVE_BUMPOUT,
  CAN_LOG_HOURS_BUMPOUT,
} from 'providers/permissions';
import { LocationProvider } from 'providers/locationProvider';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { BumpoutTimesheetFieldType } from '../views/bumpout-timesheet-list';
import {
  calculateWorkedHours,
  isJobPhaseBumpType,
  isPendingSupervisorStatus,
} from '../utils';
import { isApprovedStatus } from 'utils/bumpApproval';

export type TimeLogModalMode = 'log' | 'edit' | 'view';

type TimeLogModalProps = {
  handleClose: () => void;
  record: BumpResourceSheetDto;
  mode: TimeLogModalMode;
  onChangeField: (
    id: string,
    name: BumpoutTimesheetFieldType,
    value: DateTime | string | null,
  ) => void;
  handleRefetch: () => void;
} & DialogProps;

export const TimeLogModal = ({
  record,
  handleClose,
  mode,
  onChangeField,
  handleRefetch,
  open,
  ...rest
}: TimeLogModalProps) => {
  const classes = useStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { hasPermission } = PermissionsProvider.useContainer();
  const {
    coords: geoCoords,
    accuracy: geoAccuracy,
    error: geoError,
    loading: geoLoading,
    requestLocation,
  } = LocationProvider.useContainer();

  const canLogHours = hasPermission(CAN_LOG_HOURS_BUMPOUT);
  const canApprove = hasPermission(CAN_APPROVE_BUMPOUT);
  const isJobPhaseBump = isJobPhaseBumpType(record.bumpResourceType);

  const isBumpoutApproved = isApprovedStatus(record?.bumperApprovalStatus);

  const requiresSupervisorAction =
    isJobPhaseBump || isPendingSupervisorStatus(record?.bumperApprovalStatus);

  // Initialize from record data
  const existingTimeIn = record?.timeIn
    ? DateTime.fromISO(record.timeIn)
    : null;
  const existingTimeOut = record?.timeOut
    ? DateTime.fromISO(record.timeOut)
    : null;
  const existingWorkedHours: string | null =
    record?.loggedHours != null ? String(record.loggedHours) : null;

  const [localTimeIn, setLocalTimeIn] = useState<DateTime | null>(
    existingTimeIn,
  );
  const [localTimeOut, setLocalTimeOut] = useState<DateTime | null>(
    existingTimeOut,
  );
  const [localWorkedHours, setLocalWorkedHours] = useState<string | null>(
    existingWorkedHours,
  );
  const [isWorkedHoursOverridden, setIsWorkedHoursOverridden] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when modal opens with new record data
  useEffect(() => {
    if (open) {
      setLocalTimeIn(existingTimeIn);
      setLocalTimeOut(existingTimeOut);
      setLocalWorkedHours(existingWorkedHours);
      setIsWorkedHoursOverridden(false);
    }
  }, [open, record.id, record.timeIn, record.timeOut, record.loggedHours]);

  // Request fresh GPS coordinates when modal opens (skip view mode)
  useEffect(() => {
    if (open && mode !== 'view') {
      requestLocation();
    }
  }, [open, mode, requestLocation]);

  // Auto-calculate worked hours when times change
  useEffect(() => {
    if (localTimeIn && localTimeOut && !isWorkedHoursOverridden) {
      const calculated = calculateWorkedHours(localTimeIn, localTimeOut);
      setLocalWorkedHours(calculated.toFixed(2));
    }
  }, [localTimeIn, localTimeOut, isWorkedHoursOverridden]);

  const isViewMode = mode === 'view';

  const isApprovalMode =
    !isViewMode &&
    mode === 'edit' &&
    canApprove &&
    requiresSupervisorAction &&
    !isBumpoutApproved;

  const canSubmit = isApprovalMode
    ? (isJobPhaseBump ||
        (localWorkedHours !== null && parseFloat(localWorkedHours) > 0)) &&
      !isLoading
    : localTimeIn && localTimeOut && !isLoading;

  const canEditTimes = !isViewMode && canLogHours && !isApprovalMode;
  const canEditWorkedHours = !isViewMode && mode === 'edit' && canApprove;
  const submitLabel = isApprovalMode ? 'APPROVE' : 'LOG';

  const handleTimeInChange = (value: string) => {
    const newTime = value ? DateTime.fromISO(value) : null;
    setLocalTimeIn(newTime);
    // Reset override when times change
    if (isWorkedHoursOverridden) {
      setIsWorkedHoursOverridden(false);
    }
  };

  const handleTimeOutChange = (value: string) => {
    const newTime = value ? DateTime.fromISO(value) : null;
    setLocalTimeOut(newTime);
    if (isWorkedHoursOverridden) {
      setIsWorkedHoursOverridden(false);
    }
  };

  const handleWorkedHoursChange = (value: string) => {
    setLocalWorkedHours(value || null);
    setIsWorkedHoursOverridden(true);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsLoading(true);
    try {
      if (isApprovalMode) {
        const data: Record<string, unknown> = {
          isApprovedHours: true,
          ...(isJobPhaseBump && !localWorkedHours
            ? { approvedHours: 0 }
            : localWorkedHours
            ? { approvedHours: +localWorkedHours }
            : {}),
        };

        if (geoCoords) {
          data.approvedGeo = {
            type: 'Point',
            coordinates: [geoCoords[0], geoCoords[1]],
          };
          data.approvedGeoAccuracy = geoAccuracy;
        }

        await dataProvider.update(
          isJobPhaseBump ? '/job-phase-bump' : '/repair-payment',
          {
            id: record.id,
            data,
            previousData: record,
          },
        );

        if (localWorkedHours) {
          onChangeField(
            record.id as string,
            BumpoutTimesheetFieldType.WorkedHours,
            localWorkedHours,
          );
        }

        notify('Worked hours approved.');
      } else {
        const data: Record<string, unknown> = {
          timeIn: localTimeIn?.toFormat('HH:mm:00'),
          timeOut: localTimeOut?.toFormat('HH:mm:00'),
        };

        if (geoCoords) {
          data.loggedGeo = {
            type: 'Point',
            coordinates: [geoCoords[0], geoCoords[1]],
          };
          data.loggedGeoAccuracy = geoAccuracy;
        }

        if (canEditWorkedHours && isWorkedHoursOverridden && localWorkedHours) {
          data.loggedHours = localWorkedHours;
        }

        await dataProvider.update(
          isJobPhaseBump ? '/job-phase-bump' : '/repair-payment',
          {
            id: record.id,
            data,
            previousData: record,
          },
        );

        if (localTimeIn) {
          onChangeField(
            record.id as string,
            BumpoutTimesheetFieldType.TimeIn,
            localTimeIn,
          );
        }
        if (localTimeOut) {
          onChangeField(
            record.id as string,
            BumpoutTimesheetFieldType.TimeOut,
            localTimeOut,
          );
        }
        if (localWorkedHours) {
          onChangeField(
            record.id as string,
            BumpoutTimesheetFieldType.WorkedHours,
            localWorkedHours,
          );
        }

        notify('Worked hours logged.');
      }

      handleRefetch();
      handleClose();
    } catch (error: any) {
      notify(error.message || 'Failed to save time log', 'warning');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const getTitleText = () => {
    if (isApprovalMode) return 'Approve - Worked Hours';
    switch (mode) {
      case 'log':
        return 'Log - Worked Hours';
      case 'edit':
        return 'Edit - Worked Hours';
      case 'view':
        return 'View - Worked Hours';
    }
  };

  const formatTimeForInput = (time: DateTime | null) => {
    return time?.toFormat('HH:mm') ?? '';
  };

  const formatTimeForDisplay = (time: DateTime | null) => {
    if (!time) return '-';
    return convertTo12HourFormat(time.toFormat('HH:mm:ss'));
  };

  const formatWorkedHoursDisplay = () => {
    if (isViewMode && record?.approvedHours != null)
      return record.approvedHours;
    if (localWorkedHours === null) return '-';
    return localWorkedHours;
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="time-log-dialog-title"
      maxWidth="xs"
      fullWidth
      {...rest}
    >
      <DialogTitle id="time-log-dialog-title" className={classes.contentTitle}>
        {getTitleText()}
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {/* Time In Field */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Time In
            </Typography>
            <Show
              condition={canEditTimes}
              fallback={
                <Box display="flex" alignItems="center" mt={0.5}>
                  <AccessTimeIcon
                    fontSize="small"
                    className={classes.timeIcon}
                  />
                  <Typography>{formatTimeForDisplay(localTimeIn)}</Typography>
                </Box>
              }
            >
              <Box display="flex" alignItems="center" mt={0.5}>
                <input
                  type="time"
                  className={classes.timeInput}
                  value={formatTimeForInput(localTimeIn)}
                  onChange={e => handleTimeInChange(e.target.value)}
                />
              </Box>
            </Show>
          </Box>

          {/* Time Out Field */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Time Out
            </Typography>
            <Show
              condition={canEditTimes}
              fallback={
                <Box display="flex" alignItems="center" mt={0.5}>
                  <AccessTimeIcon
                    fontSize="small"
                    className={classes.timeIcon}
                  />
                  <Typography>{formatTimeForDisplay(localTimeOut)}</Typography>
                </Box>
              }
            >
              <Box display="flex" alignItems="center" mt={0.5}>
                <input
                  type="time"
                  className={classes.timeInput}
                  value={formatTimeForInput(localTimeOut)}
                  onChange={e => handleTimeOutChange(e.target.value)}
                />
              </Box>
            </Show>
          </Box>

          {/* Worked Hours Field */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Worked Hours
            </Typography>
            <Show
              condition={canEditWorkedHours}
              fallback={
                <Typography
                  className={`${classes.workedHoursDisplay}${
                    record?.bumperApprovalStatus ===
                    JobPhaseBumpApprovalStatus.PENDING_REVIEW
                      ? ` ${classes.pendingReviewText}`
                      : ''
                  }`}
                >
                  {formatWorkedHoursDisplay()}
                </Typography>
              }
            >
              <TextField
                type="number"
                inputProps={{
                  min: 0,
                  step: 0.5,
                  className:
                    record?.bumperApprovalStatus ===
                    JobPhaseBumpApprovalStatus.PENDING_REVIEW
                      ? classes.pendingReviewText
                      : undefined,
                }}
                value={localWorkedHours ?? ''}
                onChange={e => handleWorkedHoursChange(e.target.value)}
                className={classes.workedHoursInput}
              />
            </Show>
          </Box>
        </Box>

        {!isViewMode && (
          <GeoStatusAlert
            loading={geoLoading}
            error={geoError}
            coords={geoCoords}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Show
          condition={!isViewMode}
          fallback={
            <Button onClick={handleCancel} className={classes.cancelButton}>
              CLOSE
            </Button>
          }
        >
          <Box width="100%" display="flex" justifyContent="space-between">
            <Button onClick={handleCancel} className={classes.cancelButton}>
              CANCEL
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!canSubmit}
            >
              {isLoading ? <CircularProgress size={20} /> : submitLabel}
            </Button>
          </Box>
        </Show>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  contentTitle: {
    fontWeight: 'bold',
  },
  timeIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  timeInput: {
    padding: theme.spacing(1),
    fontSize: theme.typography.fontSize,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    width: '100%',
  },
  workedHoursDisplay: {
    marginTop: theme.spacing(0.5),
  },
  workedHoursInput: {
    maxWidth: 100,
    marginTop: theme.spacing(0.5),
  },
  pendingReviewText: {
    color: 'red',
    fontWeight: 'bold',
  },
  cancelButton: {
    color: theme.palette.error.main,
  },
}));

export default TimeLogModal;
